/**
 * App-wide transient notifications (TO-468) — the store behind `useToast`
 * (TO-644). A module store: at most {@link MAX_NOTIFICATIONS} held (pushing more
 * drops the oldest), newest first; INFO/SUCCESS auto-dismiss after
 * {@link AUTO_DISMISS_MS}, WARNING/ERROR stay until dismissed. A notification with
 * an action also stays, so its button never vanishes under the pointer or focus.
 * Rendered by `ToastProvider` at the app root.
 */
import { type NotificationAction, NotificationLevel } from '@today/ui-components'
import { useSyncExternalStore } from 'react'

export interface NotificationInput {
	level: NotificationLevel
	title: string
	description?: string
	action?: NotificationAction
}

export interface AppNotification extends NotificationInput {
	id: string
}

export const MAX_NOTIFICATIONS = 5
export const AUTO_DISMISS_MS = 5000

const AUTO_DISMISSED = new Set([NotificationLevel.INFO, NotificationLevel.SUCCESS])

let notifications: readonly AppNotification[] = []
let listeners: Array<() => void> = []
const timers = new Map<string, ReturnType<typeof setTimeout>>()
let sequence = 0

const isAutoDismissed = (input: NotificationInput) => AUTO_DISMISSED.has(input.level) && !input.action

const emit = () => {
	for (const listener of listeners) listener()
}

const clearTimer = (id: string) => {
	const timer = timers.get(id)
	if (timer !== undefined) clearTimeout(timer)
	timers.delete(id)
}

/** Show a notification; returns its id. */
export function pushNotification(input: NotificationInput): string {
	const id = `notification-${++sequence}`
	const next = [{ ...input, id }, ...notifications]
	for (const dropped of next.slice(MAX_NOTIFICATIONS)) clearTimer(dropped.id)
	notifications = next.slice(0, MAX_NOTIFICATIONS)
	if (isAutoDismissed(input)) {
		timers.set(
			id,
			setTimeout(() => dismissNotification(id), AUTO_DISMISS_MS),
		)
	}
	emit()
	return id
}

/** Remove one notification (no-op for an unknown id). */
export function dismissNotification(id: string): void {
	clearTimer(id)
	if (!notifications.some((n) => n.id === id)) return
	notifications = notifications.filter((n) => n.id !== id)
	emit()
}

/**
 * Remove a notification, then run its action (no-op for an unknown id). Removing
 * first means a second press, however fast, finds nothing and runs nothing.
 */
export function runNotificationAction(id: string): void {
	const notification = notifications.find((n) => n.id === id)
	if (!notification) return
	dismissNotification(id)
	notification.action?.onAction()
}

/** The held notifications, newest first. */
export const getNotifications = (): readonly AppNotification[] => notifications

export function subscribeNotifications(listener: () => void): () => void {
	listeners = [...listeners, listener]
	return () => {
		listeners = listeners.filter((l) => l !== listener)
	}
}

/** The live notifications, newest first. */
export const useNotifications = (): readonly AppNotification[] =>
	useSyncExternalStore(subscribeNotifications, getNotifications)

/** Test helper: empty the store and cancel its timers. */
export function resetNotifications(): void {
	for (const id of [...timers.keys()]) clearTimer(id)
	notifications = []
	emit()
}
